import { supabase } from './supabaseClient';
import { decodeJwtParts } from './debug/jwtDebug';
import { normalizeEnvKey } from './envKeyUtils';
import { getValidAccessToken } from './getValidAccessToken';

const getFunctionsUrl = (slug) => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('Missing REACT_APP_SUPABASE_URL');
  }
  return `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/${slug}`;
};

const buildAuthHeaders = async () => {
  const headers = {
    'Content-Type': 'application/json',
  };

  // See riddleApi.js / generationsApi.js: Edge Functions gateway expects JWT-style anon key in `apikey`.
  const jwtAnonRaw = normalizeEnvKey(process.env.REACT_APP_SUPABASE_JWT_ANON_KEY);
  const anonRaw = normalizeEnvKey(process.env.REACT_APP_SUPABASE_ANON_KEY);
  const jwtAnonDecoded = decodeJwtParts(jwtAnonRaw);
  const jwtAnonIsUsableJwt =
    jwtAnonDecoded?.partsCount === 3 && !jwtAnonDecoded?.decodeError && jwtAnonDecoded?.payload;

  const anonDecoded = decodeJwtParts(anonRaw);
  const anonLooksJwt =
    anonDecoded?.partsCount === 3 && !anonDecoded?.decodeError && anonDecoded?.payload;

  // Prefer explicit legacy JWT anon key; otherwise only use REACT_APP_SUPABASE_ANON_KEY if it's JWT-shaped.
  const apikeyToSend = jwtAnonIsUsableJwt ? jwtAnonRaw : anonLooksJwt ? anonRaw : null;

  const accessToken = await getValidAccessToken();

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  if (apikeyToSend) {
    headers.apikey = apikeyToSend;
  }

  return headers;
};

export const requestPowerup = async ({ type, word, gameType = null }) => {
  const headers = await buildAuthHeaders();

  const doRequest = async (reqHeaders) =>
    await fetch(getFunctionsUrl('powerup'), {
      method: 'POST',
      headers: reqHeaders,
      body: JSON.stringify({ type, word, gameType }),
    });

  let response = await doRequest(headers);

  // On mobile resume, the in-memory access token may be stale. Refresh once and retry.
  if (response.status === 401) {
    const refreshed = await getValidAccessToken({ forceRefresh: true });
    if (refreshed) {
      const retryHeaders = { ...headers, Authorization: `Bearer ${refreshed}` };
      response = await doRequest(retryHeaders);
    }
  }

  const responseText = await response.text();
  let dataJson = null;
  try {
    dataJson = responseText ? JSON.parse(responseText) : null;
  } catch (error) {
    dataJson = null;
  }

  if (!response.ok) {
    const error = new Error(dataJson?.error || responseText || 'Power-up failed.');
    error.code = dataJson?.code;
    error.status = response.status;
    throw error;
  }

  return dataJson;
};
